import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

const tabs = [
    { id: "demo", label: "Book a demo" },
    { id: "waitlist", label: "Waitlist · macOS / Linux" },
    { id: "website", label: "Request a website" },
    { id: "contact", label: "Contact" },
];

const FieldLabel = ({ children, idx }) => (
    <label className="flex items-end justify-between mb-2">
        <span className="text-sm font-medium text-ink">{children}</span>
        <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">
            {String(idx).padStart(2, "0")}
        </span>
    </label>
);

const HoneypotField = () => (
    <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
    />
);

export default function FormsSection() {
    const [tab, setTab] = useState("demo");
    const [submitting, setSubmitting] = useState("");

    useEffect(() => {
        const syncTabFromHash = () => {
            if (window.location.hash === "#website-request") {
                setTab("website");
            }
        };
        syncTabFromHash();
        window.addEventListener("hashchange", syncTabFromHash);
        return () => window.removeEventListener("hashchange", syncTabFromHash);
    }, []);

    const handleSubmit = async (e, kind) => {
        e.preventDefault();
        if (submitting) return;
        const form = e.currentTarget;
        const formData = new FormData(form);
        formData.set("form_type", kind);
        setSubmitting(kind);
        try {
            const response = await fetch("/website/form", {
                method: "POST",
                body: formData,
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload.ok) {
                throw new Error(
                    payload.message ||
                        "The message could not be sent. Please email support@reachforge.io.",
                );
            }
            const messages = {
                demo: "Demo request received.",
                waitlist: "You're on the macOS/Linux waitlist.",
                website_request: "Website request received.",
                contact: "Message sent.",
            };
            toast.success(messages[kind], {
                description:
                    "Thank you. We typically reply within one business day.",
            });
            form.reset();
        } catch (error) {
            toast.error("Could not send this message", {
                description:
                    error instanceof Error
                        ? error.message
                        : "Please email support@reachforge.io directly.",
            });
        } finally {
            setSubmitting("");
        }
    };

    return (
        <section
            id="book"
            data-testid="forms-section"
            className="border-b border-border bg-bg"
        >
            <div className="mx-auto max-w-[1440px] grid lg:grid-cols-12">
                {/* Left copy */}
                <div className="lg:col-span-5 p-6 md:p-12 lg:p-16 border-b lg:border-b-0 lg:border-r border-border bg-surface">
                    <span className="overline">Get in touch</span>
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] text-ink">
                        Start a conversation, not a sales cycle.
                    </h2>
                    <p className="mt-6 text-ink-soft max-w-md">
                        Book a 20-minute walkthrough, request macOS/Linux
                        access, or send a question. We typically reply within a
                        business day.
                    </p>

                    <div className="mt-10 grid grid-skeleton border border-border max-w-sm">
                        {[
                            { k: "20 min", v: "Live walkthrough" },
                            { k: "1 day", v: "Avg. response time" },
                            { k: "EU/UK", v: "Based in Europe" },
                        ].map((s) => (
                            <div key={s.k} className="bg-bg p-5">
                                <div className="font-display text-2xl font-bold text-ink">
                                    {s.k}
                                </div>
                                <div className="overline mt-1">{s.v}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right forms */}
                <div className="lg:col-span-7 p-6 md:p-12 lg:p-16 relative">
                    <span id="website-request" className="absolute -top-24" />
                    <div
                        role="tablist"
                        className="flex flex-wrap gap-px bg-border border border-border mb-8"
                        data-testid="forms-tablist"
                    >
                        {tabs.map((t) => (
                            <button
                                key={t.id}
                                role="tab"
                                aria-selected={tab === t.id}
                                data-testid={`forms-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`px-4 md:px-5 py-3 text-sm font-medium transition-colors ${
                                    tab === t.id
                                        ? "bg-ink text-bg"
                                        : "bg-bg text-ink-soft hover:text-ink"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === "demo" && (
                        <form
                            data-testid="form-demo"
                            onSubmit={(e) => handleSubmit(e, "demo")}
                            className="grid gap-5"
                        >
                            <HoneypotField />
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <FieldLabel idx={1}>Full name</FieldLabel>
                                    <input
                                        data-testid="demo-input-name"
                                        name="name"
                                        required
                                        type="text"
                                        className="input-sharp"
                                        placeholder="Jane Smith"
                                    />
                                </div>
                                <div>
                                    <FieldLabel idx={2}>Work email</FieldLabel>
                                    <input
                                        data-testid="demo-input-email"
                                        name="email"
                                        required
                                        type="email"
                                        className="input-sharp"
                                        placeholder="jane@company.com"
                                    />
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <FieldLabel idx={3}>Company</FieldLabel>
                                    <input
                                        data-testid="demo-input-company"
                                        name="company"
                                        type="text"
                                        className="input-sharp"
                                        placeholder="Acme Ltd."
                                    />
                                </div>
                                <div>
                                    <FieldLabel idx={4}>Team size</FieldLabel>
                                    <select
                                        data-testid="demo-input-team"
                                        name="team_size"
                                        className="input-sharp"
                                        defaultValue=""
                                    >
                                        <option value="" disabled>
                                            Select…
                                        </option>
                                        <option>Just me</option>
                                        <option>2 – 5</option>
                                        <option>6 – 20</option>
                                        <option>21+</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <FieldLabel idx={5}>
                                    What would you like to see?
                                </FieldLabel>
                                <textarea
                                    data-testid="demo-input-notes"
                                    name="message"
                                    className="input-sharp"
                                    rows={4}
                                    placeholder="Lead search for plumbers in Bristol, SMTP setup, suppression workflow…"
                                />
                            </div>
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-mute">
                                    No card. No commitment.
                                </p>
                                <button
                                    type="submit"
                                    data-testid="demo-submit"
                                    className="btn btn-primary"
                                    disabled={submitting === "demo"}
                                >
                                    {submitting === "demo"
                                        ? "Sending..."
                                        : "Request demo"}{" "}
                                    <ArrowUpRight size={14} />
                                </button>
                            </div>
                        </form>
                    )}

                    {tab === "waitlist" && (
                        <form
                            data-testid="form-waitlist"
                            onSubmit={(e) => handleSubmit(e, "waitlist")}
                            className="grid gap-5"
                        >
                            <HoneypotField />
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <FieldLabel idx={1}>Full name</FieldLabel>
                                    <input
                                        data-testid="waitlist-input-name"
                                        name="name"
                                        required
                                        type="text"
                                        className="input-sharp"
                                        placeholder="Jane Smith"
                                    />
                                </div>
                                <div>
                                    <FieldLabel idx={2}>Email</FieldLabel>
                                    <input
                                        data-testid="waitlist-input-email"
                                        name="email"
                                        required
                                        type="email"
                                        className="input-sharp"
                                        placeholder="jane@company.com"
                                    />
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <FieldLabel idx={3}>Platform</FieldLabel>
                                    <select
                                        data-testid="waitlist-input-platform"
                                        name="platform"
                                        className="input-sharp"
                                        defaultValue="macOS"
                                    >
                                        <option>macOS</option>
                                        <option>Linux</option>
                                        <option>Both</option>
                                    </select>
                                </div>
                                <div>
                                    <FieldLabel idx={4}>
                                        Primary use case
                                    </FieldLabel>
                                    <input
                                        data-testid="waitlist-input-usecase"
                                        name="use_case"
                                        type="text"
                                        className="input-sharp"
                                        placeholder="Agency prospecting"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-mute">
                                    We'll only email you about availability.
                                </p>
                                <button
                                    type="submit"
                                    data-testid="waitlist-submit"
                                    className="btn btn-primary"
                                    disabled={submitting === "waitlist"}
                                >
                                    {submitting === "waitlist"
                                        ? "Sending..."
                                        : "Join waitlist"}{" "}
                                    <ArrowUpRight size={14} />
                                </button>
                            </div>
                        </form>
                    )}

                    {tab === "website" && (
                        <form
                            data-testid="form-website-request"
                            onSubmit={(e) => handleSubmit(e, "website_request")}
                            className="grid gap-5"
                        >
                            <HoneypotField />
                            <input
                                type="hidden"
                                name="subject"
                                value="Website request"
                            />
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <FieldLabel idx={1}>Full name</FieldLabel>
                                    <input
                                        data-testid="website-input-name"
                                        name="name"
                                        required
                                        type="text"
                                        className="input-sharp"
                                        placeholder="Jane Smith"
                                    />
                                </div>
                                <div>
                                    <FieldLabel idx={2}>Email</FieldLabel>
                                    <input
                                        data-testid="website-input-email"
                                        name="email"
                                        required
                                        type="email"
                                        className="input-sharp"
                                        placeholder="jane@company.com"
                                    />
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <FieldLabel idx={3}>
                                        Company / project
                                    </FieldLabel>
                                    <input
                                        data-testid="website-input-company"
                                        name="company"
                                        type="text"
                                        className="input-sharp"
                                        placeholder="Acme Ltd."
                                    />
                                </div>
                                <div>
                                    <FieldLabel idx={4}>Website type</FieldLabel>
                                    <input
                                        data-testid="website-input-usecase"
                                        name="use_case"
                                        type="text"
                                        className="input-sharp"
                                        placeholder="Business website, landing page, booking site…"
                                    />
                                </div>
                            </div>
                            <div>
                                <FieldLabel idx={5}>
                                    What do you need the website to do?
                                </FieldLabel>
                                <textarea
                                    data-testid="website-input-message"
                                    name="message"
                                    required
                                    className="input-sharp"
                                    rows={5}
                                    placeholder="Tell us about your business, target customers, pages needed, timeline, and any existing website."
                                />
                            </div>
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-mute">
                                    Share the goal. We will reply with practical
                                    next steps.
                                </p>
                                <button
                                    type="submit"
                                    data-testid="website-submit"
                                    className="btn btn-primary"
                                    disabled={submitting === "website_request"}
                                >
                                    {submitting === "website_request"
                                        ? "Sending..."
                                        : "Submit request"}{" "}
                                    <ArrowUpRight size={14} />
                                </button>
                            </div>
                        </form>
                    )}

                    {tab === "contact" && (
                        <form
                            data-testid="form-contact"
                            onSubmit={(e) => handleSubmit(e, "contact")}
                            className="grid gap-5"
                        >
                            <HoneypotField />
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <FieldLabel idx={1}>Name</FieldLabel>
                                    <input
                                        data-testid="contact-input-name"
                                        name="name"
                                        required
                                        type="text"
                                        className="input-sharp"
                                        placeholder="Jane Smith"
                                    />
                                </div>
                                <div>
                                    <FieldLabel idx={2}>Email</FieldLabel>
                                    <input
                                        data-testid="contact-input-email"
                                        name="email"
                                        required
                                        type="email"
                                        className="input-sharp"
                                        placeholder="jane@company.com"
                                    />
                                </div>
                            </div>
                            <div>
                                <FieldLabel idx={3}>Subject</FieldLabel>
                                <input
                                    data-testid="contact-input-subject"
                                    name="subject"
                                    type="text"
                                    className="input-sharp"
                                    placeholder="Licence key, billing, setup help…"
                                />
                            </div>
                            <div>
                                <FieldLabel idx={4}>Message</FieldLabel>
                                <textarea
                                    data-testid="contact-input-message"
                                    name="message"
                                    required
                                    className="input-sharp"
                                    rows={5}
                                    placeholder="How can we help?"
                                />
                            </div>
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-mute">
                                    Or email{" "}
                                    <a
                                        href="mailto:support@reachforge.io"
                                        className="text-ink link-underline"
                                    >
                                        support@reachforge.io
                                    </a>
                                </p>
                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    className="btn btn-primary"
                                    disabled={submitting === "contact"}
                                >
                                    {submitting === "contact"
                                        ? "Sending..."
                                        : "Send message"}{" "}
                                    <ArrowUpRight size={14} />
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
